import DialogCreateListing from "@/components/listing/dialog-create-listing";
import { ListingDTO, ListingOwnerType, ListingType } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type OpenCreateListingDialogConfig = {
  defaultListingType?: ListingType;
  defaultOwnerType?: ListingOwnerType;
  defaultAssigneeId?: string;
  searchQuery?: string;
  /** Items the new list is created with, in the same request. */
  companiesIds?: readonly string[];
  employeesIds?: readonly string[];
  onCreated?: (listing: ListingDTO) => void | Promise<void>;
};

type CreateListingDialogContextType = {
  openCreateListingDialog: (config: OpenCreateListingDialogConfig) => void;
};

const CreateListingDialogContext =
  createContext<CreateListingDialogContextType | null>(null);

export const useCreateListingDialog = (): CreateListingDialogContextType => {
  const ctx = useContext(CreateListingDialogContext);
  if (!ctx) {
    throw new Error(
      "useCreateListingDialog must be used within CreateListingDialogProvider"
    );
  }
  return ctx;
};

type ActiveDialog = OpenCreateListingDialogConfig & { instanceKey: number };

/**
 * Renders DialogCreateListing at a stable mount point so its lifecycle is
 * decoupled from callers that may unmount (e.g. table cells re-rendered by
 * silent data refetches).
 */
export const CreateListingDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [active, setActive] = useState<ActiveDialog | null>(null);

  const openCreateListingDialog = useCallback(
    (config: OpenCreateListingDialogConfig) => {
      setActive({ ...config, instanceKey: Date.now() });
    },
    []
  );

  const handleClose = useCallback(() => {
    setActive(null);
  }, []);

  const handleCreated = useCallback(
    async (listing: ListingDTO) => {
      const onCreated = active?.onCreated;
      setActive(null);
      if (onCreated) {
        await onCreated(listing);
      }
    },
    [active]
  );

  const value = useMemo(
    () => ({ openCreateListingDialog }),
    [openCreateListingDialog]
  );

  return (
    <CreateListingDialogContext.Provider value={value}>
      {children}
      {active && (
        <DialogCreateListing
          key={active.instanceKey}
          defaultListingType={active.defaultListingType}
          defaultOwnerType={active.defaultOwnerType}
          defaultAssigneeId={active.defaultAssigneeId}
          searchQuery={active.searchQuery}
          companiesIds={active.companiesIds}
          employeesIds={active.employeesIds}
          onClose={handleClose}
          onCreated={handleCreated}
        />
      )}
    </CreateListingDialogContext.Provider>
  );
};
